/**
 * Public-gateway IPFS helpers for SportsFuture applications.
 * The chain only stores contentHash + ipfs://cid. No Supabase.
 * Pinning happens server-side (netlify/functions/pinata-pin, see serverApi.ts):
 * no Pinata key is ever shipped to the browser.
 */

/**
 * Public IPFS gateways, fastest first. Each one answers with
 * `Access-Control-Allow-Origin: *` and needs no token or custom header, so
 * browser fetches from any SportsFuture origin skip the CORS preflight.
 * Measured 2026-10-24: Filebase ~1s, Pinata public ~5-7s, dweb.link often 429.
 * Do not add dedicated Pinata gateways here: they are origin-restricted to
 * OpenPlaza and need a gateway token that would ship in the browser bundle.
 */
export const DEFAULT_IPFS_GATEWAYS = [
  'https://ipfs.filebase.io/ipfs/',
  'https://gateway.pinata.cloud/ipfs/',
  'https://dweb.link/ipfs/',
] as const;

/** Per-gateway budget for JSON fetches so a dead gateway cannot stall a page. */
export const IPFS_FETCH_TIMEOUT_MS = 8000;

export const MAX_REGISTRY_URI_LENGTH = 256;

/** Host or URL → `https://host/ipfs/` */
export function normalizeGatewayBase(raw: string): string {
  let s = raw.trim();
  if (!s) throw new Error('Empty IPFS gateway');
  if (!/^https?:\/\//i.test(s)) s = `https://${s}`;
  s = s.replace(/\/+$/, '');
  if (!/\/ipfs$/i.test(s)) s = `${s}/ipfs`;
  return `${s}/`;
}

export function configuredIpfsGateways(): string[] {
  return [...DEFAULT_IPFS_GATEWAYS];
}

export function cidFromUri(uri: string): string {
  const trimmed = uri.trim();
  if (trimmed.startsWith('ipfs://')) return trimmed.slice('ipfs://'.length);
  const match = trimmed.match(/\/ipfs\/([^/?#]+)/);
  if (match) return match[1];
  if (/^(bafy|Qm)[a-zA-Z0-9]+$/.test(trimmed)) return trimmed;
  throw new Error(`Not an IPFS URI: ${trimmed.slice(0, 80)}`);
}

export function ipfsUri(cid: string): string {
  const clean = cid.replace(/^ipfs:\/\//, '').replace(/^\/ipfs\//, '');
  if (!clean) throw new Error('Empty IPFS CID');
  const uri = `ipfs://${clean}`;
  if (uri.length > MAX_REGISTRY_URI_LENGTH) {
    throw new Error(`IPFS URI exceeds registry max (${uri.length} > ${MAX_REGISTRY_URI_LENGTH})`);
  }
  return uri;
}

export function gatewayUrl(cidOrUri: string, gatewayBase: string): string {
  const cid = cidFromUri(cidOrUri);
  const base = gatewayBase.includes('/ipfs')
    ? gatewayBase.endsWith('/')
      ? gatewayBase
      : `${gatewayBase}/`
    : normalizeGatewayBase(gatewayBase);
  return `${base}${cid}`;
}

/** Browser-safe media URL on the first public gateway (never ipfs.io). */
export function publicIpfsMediaUrl(cidOrUri: string): string {
  const gateways = configuredIpfsGateways();
  return gatewayUrl(cidOrUri, gateways[0] ?? DEFAULT_IPFS_GATEWAYS[0]);
}

/**
 * Rewrite explorer / ipfs.io / bare-CID image URLs onto the first public gateway.
 * Leaves non-IPFS http(s) URLs (Pexels, etc.) unchanged.
 */
export function toPinataMediaUrl(urlOrCid: string | null | undefined): string | null {
  if (!urlOrCid) return null;
  const trimmed = urlOrCid.trim();
  if (!trimmed) return null;
  try {
    return publicIpfsMediaUrl(trimmed);
  } catch {
    if (/^https?:\/\//i.test(trimmed) && !/ipfs\.io|cloudflare-ipfs\.com/i.test(trimmed)) {
      return trimmed;
    }
    return null;
  }
}

/** Ordered media URLs for img onError failover. */
export function pinataMediaUrls(urlOrCid: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const gw of configuredIpfsGateways()) {
    try {
      const url = gatewayUrl(urlOrCid, gw);
      if (!seen.has(url)) {
        seen.add(url);
        out.push(url);
      }
    } catch {
      /* skip */
    }
  }
  return out;
}

export function nextPinataMediaUrl(currentUrl: string): string | null {
  try {
    const cid = cidFromUri(currentUrl);
    const urls = pinataMediaUrls(cid);
    const currentPath = currentUrl.split('?')[0];
    const idx = urls.findIndex((u) => u === currentPath);
    if (idx === -1) return urls[0] ?? null;
    return urls[idx + 1] ?? null;
  } catch {
    return null;
  }
}

/**
 * GET JSON from the gateways in order. No custom request headers (a simple
 * CORS request, no preflight) and a per-gateway timeout.
 */
export async function fetchIpfsJson<T = unknown>(
  cidOrUri: string,
  opts?: { gateways?: string[]; fetchImpl?: typeof fetch; timeoutMs?: number }
): Promise<T> {
  const gateways = opts?.gateways ?? configuredIpfsGateways();
  const fetchImpl = opts?.fetchImpl ?? fetch;
  const timeoutMs = opts?.timeoutMs ?? IPFS_FETCH_TIMEOUT_MS;
  const errors: string[] = [];
  for (const gw of gateways) {
    const url = gatewayUrl(cidOrUri, gw);
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
    try {
      const res = await fetchImpl(url, controller ? { signal: controller.signal } : undefined);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as T;
    } catch (err) {
      const aborted = controller?.signal.aborted;
      errors.push(
        `${url}: ${aborted ? `timeout after ${timeoutMs}ms` : err instanceof Error ? err.message : String(err)}`
      );
    } finally {
      if (timer) clearTimeout(timer);
    }
  }
  throw new Error(`IPFS fetch failed: ${errors.join(' | ')}`);
}
